/**
 * Wraps `extractSpeakerIntervals` so both zoom-call entry points have a
 * single import path with a domain-friendly type name. The underlying
 * splitting logic stays in `speakerIntervalUtils.ts`.
 */

import {
  extractSpeakerIntervals,
  type TranscriptWord,
} from '@src/services/framing/speakerIntervalUtils';

import type { SceneDetection } from '@src/editor/types/sceneDetection';

export interface SpeakerTurn {
  speakerLabel: string;
  /** Media time in seconds. */
  startTime: number;
  /** Media time in seconds. */
  endTime: number;
}

export function buildSpeakerTurns(
  transcript: ReadonlyArray<TranscriptWord>,
): SpeakerTurn[] {
  // `extractSpeakerIntervals` also splits a speaker on any pause longer than
  // 0.5s, which would cut one uninterrupted monologue into many scenes. A
  // turn only ends when someone else starts talking, so merge those back.
  const turns: SpeakerTurn[] = [];
  for (const { speakerLabel, startTime, endTime } of extractSpeakerIntervals(
    transcript,
  )) {
    const last = turns[turns.length - 1];
    if (last && last.speakerLabel === speakerLabel) {
      last.endTime = Math.max(last.endTime, endTime);
    } else {
      turns.push({ speakerLabel, startTime, endTime });
    }
  }
  return turns;
}

type BoundaryKind = 'edge' | 'cut' | 'speaker';

/**
 * Scene ranges (media time) for "one scene per speaker", also split at every
 * camera cut in `shots` — a scene gets a single crop, so one that ran across a
 * cut (close-up → wide) would frame the second shot with the first shot's crop.
 *
 * Boundaries go where the speaker changes (the end of the outgoing turn) and
 * at each cut. Cutting at both leaves slivers with nobody talking, e.g. a turn
 * ending at 23s, a cut at 25s and the next speaker starting at 26s gives an
 * empty 23–25s piece. A speechless piece that starts at a speaker change is
 * the same shot as the scene before it, so it merges into that scene instead
 * of standing alone. A speechless piece bounded by cuts on both sides is a
 * whole shot of its own and stays a scene.
 */
export function buildSpeakerScenes(
  transcript: ReadonlyArray<TranscriptWord>,
  shots: ReadonlyArray<SceneDetection> = [],
): SceneDetection[] {
  const speech = extractSpeakerIntervals(transcript);
  const turns = buildSpeakerTurns(transcript);
  if (turns.length === 0) return [];

  const cuts = shots.map(s => s.startTime);
  const start = Math.min(turns[0].startTime, ...cuts);
  const end = Math.max(
    turns[turns.length - 1].endTime,
    ...shots.map(s => s.startTime + s.lengthSeconds),
  );

  const kindByTime = new Map<number, BoundaryKind>([
    [start, 'edge'],
    [end, 'edge'],
  ]);
  for (const turn of turns.slice(0, -1)) {
    kindByTime.set(turn.endTime, 'speaker');
  }
  // A cut wins over a speaker change at the same instant: it's a new shot.
  for (const cut of cuts) {
    if (cut > start && cut < end) kindByTime.set(cut, 'cut');
  }
  const boundaries = [...kindByTime.entries()]
    .map(([time, kind]) => ({ time, kind }))
    .sort((a, b) => a.time - b.time);

  const scenes: Array<{ startTime: number; endTime: number }> = [];
  let speechIdx = 0;
  for (let i = 0; i < boundaries.length - 1; i++) {
    const from = boundaries[i];
    const to = boundaries[i + 1].time;

    while (
      speechIdx < speech.length &&
      speech[speechIdx].endTime <= from.time
    ) {
      speechIdx++;
    }
    const hasSpeech =
      speechIdx < speech.length && speech[speechIdx].startTime < to;

    const previous = scenes[scenes.length - 1];
    if (!hasSpeech && from.kind === 'speaker' && previous) {
      previous.endTime = to;
    } else {
      scenes.push({ startTime: from.time, endTime: to });
    }
  }

  return scenes.map(({ startTime, endTime }) => ({
    startTime,
    lengthSeconds: endTime - startTime,
  }));
}
